import {
  Button,
  Divider,
  Menu,
  MenuDivider,
  MenuItem,
  NonIdealState,
  Popover,
  Tag,
  Tooltip,
} from '@blueprintjs/core';
import noSMSSVG from '../../../assets/noSMS.svg';
import { useSMSState } from './useSMSState';
import {
  lock_hooks,
  useApplicationDetail,
  useCustomer,
  sms_communication_template_hooks,
  sms_communication_hooks,
  useGetConsentItemsByCustomer,
  useGetConsentPartners,
} from '@axo/shared/data-access/hooks';
import { SMSCardEdit } from './SMSCardEdit';
import { SMSCardView } from './SMSCardView';
import { CommunicationCount } from '../Panel';
import panelClasses from '../Panel/Panel.module.css';
import { sms } from '@axo/shared/data-access/types';
import { useState } from 'react';
import styles from './SMSPanel.module.css';
import noEmailSVG from '../../../assets/noEmails.svg';
import { OrttoSMSCard } from './OrttoSMSCard.tsx';
import { renderPhoneNumber } from '@axo/shared/util/formatters';
import { MarketCountryCode } from '@axo/shared/types';

enum SMSType {
  ACE = 'ACE',
  ORTTO = 'ORTTO',
}
export function SMSPanel({ applicationID }: { applicationID: string }) {
  const SMSListState = useSMSState(applicationID);
  const application = useApplicationDetail(applicationID);
  const customer = useCustomer(application.data?.CustomerID);
  const smsTemplates = sms_communication_template_hooks.useSMSTemplates(
    application.data?.MarketCountry ?? '',
    application.data?.Meta?.Broker ?? 0,
  );
  const hasLock = lock_hooks.useCurrentUserHasLock(
    applicationID,
    application.data?.MarketCountry,
  );
  const orttoSMSs = sms_communication_hooks.useOrttoSMS(
    application.data?.CustomerID,
    application.data?.MarketCountry,
  );
  const orttoSMSBodies = sms_communication_hooks.useOrttoSMSBodies(
    application.data?.CustomerID,
    application.data?.MarketCountry,
  );
  const [smsType, setSMSType] = useState(SMSType.ACE);

  const getBody = (smsBodyID: string) => {
    return orttoSMSBodies.data?.Items.find((x) => x.ID === smsBodyID);
  };

  const getTemplate = (templateID?: string) => {
    if (!smsTemplates.data || !templateID) return;
    return smsTemplates.data.Items.find((x) => x.ID === templateID);
  };

  const consentPartners = useGetConsentPartners();
  const partnerID = consentPartners.data?.find(
    (x) =>
      x.BrandID === application.data?.Meta?.Brand &&
      Boolean(application.data.Meta.Brand),
  )?.ID;
  const consentItems = useGetConsentItemsByCustomer(
    application.data?.MarketCountry as MarketCountryCode,
    application.data?.CustomerID,
  );

  const getTemplateStatus = (template: sms.SMSTemplate) => {
    // FIXME: This is a hotfix until correct behavior is determined
    return { disabled: false, text: '' };

    if (
      template.Transactional &&
      consentItems.data?.Items?.find(
        (x) =>
          x.PartnerID === partnerID &&
          x.Country === application.data?.MarketCountry &&
          x.Transactional &&
          x.Channel === 'sms' &&
          x.State === 'given',
      )
    )
      return { disabled: false, text: 'Consent given for this template' };

    if (
      !template.Transactional &&
      consentItems.data?.Items?.find(
        (x) =>
          x.PartnerID === partnerID &&
          x.Country === application.data?.MarketCountry &&
          !x.Transactional &&
          x.Channel === 'sms' &&
          x.State === 'given',
      )
    )
      return { disabled: false, text: 'Consent given for this template' };

    return {
      disabled: true,
      text: 'Missing consent or template not allowed',
    };
  };

  return (
    <div className={panelClasses.panel}>
      <div className={styles.tags}>
        <Tag
          round
          minimal={smsType !== SMSType.ACE}
          interactive
          large
          intent={smsType === SMSType.ACE ? 'primary' : 'none'}
          onClick={() => setSMSType(SMSType.ACE)}
        >
          ACE
        </Tag>
        <Tag
          round
          minimal={smsType !== SMSType.ORTTO}
          interactive
          large
          intent={smsType === SMSType.ORTTO ? 'primary' : 'none'}
          onClick={() => setSMSType(SMSType.ORTTO)}
        >
          Ortto
        </Tag>
      </div>
      {smsType === SMSType.ACE && (
        <>
          <CommunicationCount
            label="Number of ACE SMS sent in total:"
            count={SMSListState.state.length}
          />
          <div className={panelClasses.panelActions}>
            <Popover
              interactionKind="hover"
              content={
                <Menu>
                  <MenuDivider title="Select a template" />
                  {smsTemplates.data?.Items.map((template) => (
                    <MenuItem
                      disabled={
                        getTemplateStatus(template).disabled || !hasLock.data
                      }
                      key={template.ID}
                      text={
                        <Tooltip
                          disabled={!getTemplateStatus(template).disabled}
                          content={
                            <span>{getTemplateStatus(template).text}</span>
                          }
                        >
                          {template.Name}
                        </Tooltip>
                      }
                      onClick={() => SMSListState.addNewSMS(template)}
                    />
                  ))}
                </Menu>
              }
            >
              <Button disabled={!hasLock.data} icon="comment" text="New SMS" />
            </Popover>
          </div>
          <Divider />
          <div className={panelClasses.cardContainer}>
            {!SMSListState.hasLoaded ? (
              <NonIdealState>
                <p>Loading SMS...</p>
              </NonIdealState>
            ) : null}

            {SMSListState.hasLoaded && !SMSListState.state.length ? (
              <NonIdealState icon={<img src={noSMSSVG} alt="No SMS" />}>
                <p>There are no SMS added yet.</p>
              </NonIdealState>
            ) : null}

            {[
              ...SMSListState.state.filter((x) => x.isNew),
              ...SMSListState.state.filter((x) => !x.isNew),
            ].map((SMS) => {
              const template = getTemplate(SMS.sms?.TemplateID);
              if (SMS.isNew && application.data) {
                return (
                  <SMSCardEdit
                    key={SMS.sms.ID}
                    SMS={SMS.sms}
                    to={customer.data?.MobilePhoneNumber || ''}
                    setSMS={SMSListState.editSMS(SMS)}
                    saveSMS={() => SMSListState.saveSMS(SMS)}
                    cancelSMS={() => SMSListState.cancelSMS(SMS)}
                    template={template}
                    applicationID={applicationID}
                    marketCountry={application.data.MarketCountry}
                  />
                );
              }

              if (SMS.sms !== null && !SMS.isNew) {
                return (
                  <SMSCardView
                    key={SMS.sms.ID}
                    SMS={SMS.sms}
                    template={template}
                    to={renderPhoneNumber({
                      phoneNumber: customer.data?.MobilePhoneNumber,
                      phoneCountryCode: String(
                        customer.data?.MobilePhoneCountryCode,
                      ),
                    })}
                    markAsResolved={() => SMSListState.markAsResolved(SMS)}
                  />
                );
              }

              return null;
            })}
          </div>
        </>
      )}
      {smsType === SMSType.ORTTO && (
        <>
          <CommunicationCount
            label="Number of Ortto SMS sent in total:"
            count={orttoSMSs.data?.Items?.length ?? 0}
          />
          <Divider />
          <div className={panelClasses.cardContainer}>
            {orttoSMSs.isLoading ? (
              <NonIdealState>
                <p>Loading ortto SMSs...</p>
              </NonIdealState>
            ) : null}

            {!orttoSMSs.isLoading && !orttoSMSs.data?.Items.length ? (
              <NonIdealState
                icon={<img src={noEmailSVG} alt="No ortto SMSs" />}
              >
                <p>There are no ortto SMSs added yet.</p>
              </NonIdealState>
            ) : null}
            {orttoSMSs.data &&
              orttoSMSs.data.Items.map((orttoSMS) => {
                return (
                  <OrttoSMSCard
                    key={orttoSMS.ID}
                    sms={orttoSMS}
                    body={getBody(orttoSMS.SMSBodyID)}
                  />
                );
              })}
          </div>
        </>
      )}
    </div>
  );
}
